/**
 * PlayerDetail — SLAM Player Profile Page
 *
 * Route: /basketball/gm/player/:playerName
 * Data: /api/basketball/portal/player/:playerName (DraftExpress + NatStat)
 *
 * Three tabs: Overview, Season Stats, Athletic History
 * Hero section: Headshot + SLAM Composite ring + COG/CHAR/HDI sub-rings
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { C, tier, Ring, AnimN, Pill, Bar, Radar, TrendChart } from '../../components/design-system/primitives';
import CBBHeadshot from '../../components/CBBHeadshot';
import { searchPlayerHeadshot } from '../../lib/cbbHeadshotService';
import { ArrowLeft, Loader2, MapPin, GraduationCap, Ruler, Weight, Calendar } from 'lucide-react';
import config from '../../utils/config';

const API_BASE = config.SERVER_BASE_URL;

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/** Derive COG (cognitive/IQ) score from stats — weighted APG, PER, efficiency. */
function deriveCogScore(stats) {
  if (!stats) return null;
  const apgFactor = Math.min((stats.apg || 0) / 8, 1) * 35;
  const perFactor = Math.min((stats.per || 0) / 30, 1) * 40;
  const a40Factor = Math.min((stats.a40 || 0) / 12, 1) * 25;
  return Math.round(Math.min(apgFactor + perFactor + a40Factor, 99));
}

/** Derive CHAR (character) score — placeholder heuristic from DX eval keywords. */
function deriveCharScore(dxEval) {
  if (!dxEval) return null;
  const lower = dxEval.toLowerCase();
  let score = 65;
  const positives = ['coachable', 'work ethic', 'leadership', 'competitive', 'effort', 'cerebral', 'communicator', 'respect', 'culture'];
  const negatives = ['inconsistent', 'poor attitude', 'selfish', 'lazy', 'undisciplined'];
  positives.forEach(k => { if (lower.includes(k)) score += 4; });
  negatives.forEach(k => { if (lower.includes(k)) score -= 5; });
  return Math.round(Math.min(Math.max(score, 30), 99));
}

function statOrDash(v, dec = 1) {
  if (v === null || v === undefined || v === 0) return '--';
  return typeof v === 'number' ? v.toFixed(dec) : v;
}

// ═══════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function MetaPill({ icon: Icon, label }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', borderRadius: 100,
      background: C.s2, border: `1px solid ${C.border}`,
      fontSize: 11, color: C.t2,
    }}>
      {Icon && <Icon size={11} style={{ color: C.t3 }} />}
      {label}
    </span>
  );
}

function StatBox({ label, value, highlight }) {
  return (
    <div style={{
      background: C.s2, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: '10px 12px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 8, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{
        fontSize: 20, fontWeight: 200, fontFamily: 'monospace',
        color: highlight ? C.gold : C.text, letterSpacing: -1,
      }}>
        {value}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: OVERVIEW
// ═══════════════════════════════════════════════════════════════════════════

function OverviewTabContent({ player, cogScore, charScore }) {
  const dxGrade = player.dxGrade || 0;

  const radarData = {
    Scoring: Math.min(Math.round((player.stats?.ppg || 0) * 4), 99),
    Playmaking: Math.min(Math.round((player.stats?.apg || 0) * 10), 99),
    Rebounding: Math.min(Math.round((player.stats?.rpg || 0) * 8), 99),
    Efficiency: Math.min(Math.round((player.stats?.per || 0) * 3.5), 99),
    Shooting: Math.min(Math.round((player.stats?.fg3Pct || 0) * 2), 99),
  };

  const trendData = [
    { d: 'Pre', s: Math.max(dxGrade - 12, 40) },
    { d: 'Fr', s: Math.max(dxGrade - 8, 45) },
    { d: 'So', s: Math.max(dxGrade - 3, 50) },
    { d: 'Now', s: dxGrade },
  ];

  const pillarBars = [
    { label: 'DraftExpress Grade', value: dxGrade, color: tier(dxGrade).color },
    { label: 'Cognitive (COG)', value: cogScore || 0, color: C.cog },
    { label: 'Character (CHAR)', value: charScore || 0, color: C.char },
    { label: 'HDI (Coming Soon)', value: 0, color: C.hdi },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Pillar Breakdown */}
      <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
          SLAM Pillar Breakdown
        </div>
        {pillarBars.map(b => (
          <Bar key={b.label} label={b.label} value={b.value} color={b.color} />
        ))}
      </div>

      {/* Radar + Trend side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
            Skill Profile
          </div>
          <Radar data={radarData} color={C.gold} sz={220} />
        </div>
        <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
          <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
            Score Trajectory
          </div>
          <TrendChart data={trendData} min={Math.max(trendData[0].s - 10, 30)} max={Math.min(dxGrade + 10, 100)} />
        </div>
      </div>

      {/* DX Scouting Narrative */}
      {player.dxEval && (
        <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
          <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 12 }}>
            DraftExpress Scouting Report
          </div>
          <p style={{ fontSize: 13, color: C.t2, lineHeight: 1.8, margin: 0 }}>
            {player.dxEval}
          </p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: SEASON STATS
// ═══════════════════════════════════════════════════════════════════════════

function StatsTabContent({ player }) {
  const s = player.stats || {};
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Box Score */}
      <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
          Box Score Averages
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          <StatBox label="PPG" value={statOrDash(s.ppg)} highlight />
          <StatBox label="RPG" value={statOrDash(s.rpg)} />
          <StatBox label="APG" value={statOrDash(s.apg)} />
          <StatBox label="MPG" value={statOrDash(s.mpg)} />
          <StatBox label="GP" value={s.gamesPlayed || '--'} />
        </div>
      </div>

      {/* Shooting */}
      <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
          Shooting Splits
        </div>
        <div className="grid grid-cols-3 gap-3">
          <StatBox label="FG%" value={s.fgPct ? `${statOrDash(s.fgPct)}%` : '--'} />
          <StatBox label="3P%" value={s.fg3Pct ? `${statOrDash(s.fg3Pct)}%` : '--'} />
          <StatBox label="FT%" value={s.ftPct ? `${statOrDash(s.ftPct)}%` : '--'} />
        </div>
      </div>

      {/* Advanced */}
      <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
          Advanced / Per-40 Minutes
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatBox label="PER" value={statOrDash(s.per)} highlight />
          <StatBox label="P40" value={statOrDash(s.p40)} />
          <StatBox label="R40" value={statOrDash(s.r40)} />
          <StatBox label="A40" value={statOrDash(s.a40)} />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: ATHLETIC HISTORY
// ═══════════════════════════════════════════════════════════════════════════

function HistoryTabContent({ player }) {
  const history = [
    player.school && { year: player.season || '2025-26', event: `${player.classYear} at ${player.school}`, detail: `DX Grade: ${player.dxGrade || '--'} • ${player.archetype || 'N/A'}` },
    player.yearsLeft && { year: 'Eligibility', event: `${player.yearsLeft} year${player.yearsLeft > 1 ? 's' : ''} remaining`, detail: `Portal Status: ${player.portalStatus || 'Unknown'}` },
    player.marketValue && { year: 'NIL', event: `Market Value: $${player.marketValue.toLocaleString()}`, detail: 'Estimated annual NIL valuation' },
  ].filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: C.s1, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 9, color: C.t3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 20 }}>
          Athletic Timeline
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {history.map((h, i) => (
            <div key={i} style={{ display: 'flex', gap: 16, position: 'relative' }}>
              {/* Timeline line + dot */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                  background: i === 0 ? C.gold : C.s3,
                  border: `2px solid ${i === 0 ? C.gold : C.border}`,
                }} />
                {i < history.length - 1 && (
                  <div style={{ width: 1, flex: 1, background: C.border, minHeight: 28 }} />
                )}
              </div>
              {/* Content */}
              <div style={{ paddingBottom: 20, flex: 1 }}>
                <div style={{ fontSize: 10, color: C.gold, fontWeight: 600, letterSpacing: 1, marginBottom: 2 }}>
                  {h.year}
                </div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 400, marginBottom: 2 }}>
                  {h.event}
                </div>
                <div style={{ fontSize: 11, color: C.t2 }}>
                  {h.detail}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* HDI Placeholder */}
      <div style={{
        background: C.s1, border: `1px dashed ${C.border}`, borderRadius: 16, padding: 32,
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 9, color: C.hdi, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
          Human Development Index (HDI)
        </div>
        <p style={{ fontSize: 12, color: C.t2, maxWidth: 360, margin: '0 auto', lineHeight: 1.6 }}>
          HDI scores track academic progress, community engagement, and personal development.
          This module is under development and will be available in a future release.
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function PlayerDetail() {
  const { playerName: rawName } = useParams();
  const navigate = useNavigate();
  const playerName = decodeURIComponent(rawName || '');

  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [espnId, setEspnId] = useState(null);

  // Fetch player data
  useEffect(() => {
    if (!playerName) return;
    setLoading(true);
    setError(null);

    fetch(`${API_BASE}/api/basketball/portal/player/${encodeURIComponent(playerName)}`)
      .then(res => res.json())
      .then(json => {
        const d = json.data || json;
        if (!d || (!d.playerName && !d.player_name)) {
          setError('Player not found');
        } else {
          setPlayer(d);
        }
      })
      .catch(err => {
        console.error('[PlayerDetail] Fetch error:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [playerName]);

  // Look up ESPN headshot
  useEffect(() => {
    if (!playerName) return;
    searchPlayerHeadshot(playerName, 'mens')
      .then(result => { if (result?.espnId) setEspnId(result.espnId); })
      .catch(() => {});
  }, [playerName]);

  // Derived scores
  const cogScore = player ? deriveCogScore(player.stats) : null;
  const charScore = player ? deriveCharScore(player.dxEval) : null;
  const slamComposite = player?.dxGrade || 0;
  const slamTier = tier(slamComposite);

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'stats', label: 'Season Stats' },
    { id: 'history', label: 'Athletic History' },
  ];

  if (loading) {
    return (
      <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={24} className="animate-spin" style={{ color: C.gold }} />
      </div>
    );
  }

  if (error || !player) {
    return (
      <div style={{ background: C.bg, minHeight: '100vh', padding: 40, color: C.text, fontFamily: "'Inter', system-ui" }}>
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: C.t2, cursor: 'pointer', fontSize: 12, marginBottom: 20 }}
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ textAlign: 'center', paddingTop: 80 }}>
          <div style={{ fontSize: 18, color: C.t2, marginBottom: 8 }}>
            {error || 'Player not found'}
          </div>
          <div style={{ fontSize: 12, color: C.t3 }}>
            Could not load data for "{playerName}"
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: '100vh', padding: '24px 24px 60px', color: C.text, fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', color: C.t2, cursor: 'pointer',
          fontSize: 12, marginBottom: 20, padding: 0,
        }}
      >
        <ArrowLeft size={14} /> Back to Dashboard
      </button>

      {/* ═══ HERO SECTION ═══ */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 24,
        alignItems: 'center', marginBottom: 32,
        paddingBottom: 24, borderBottom: `1px solid ${C.border}`,
      }}>
        {/* Left: Headshot + Bio */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flex: '1 1 300px' }}>
          <CBBHeadshot
            espnId={espnId}
            playerName={player.playerName}
            size="MEDIUM"
            gender="mens"
            style={{ borderRadius: '50%', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontSize: 9, fontWeight: 600, color: C.gold, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 4 }}>
              Player Profile
            </div>
            <div style={{ fontSize: 28, fontWeight: 200, letterSpacing: -1.5, lineHeight: 1.1, marginBottom: 8 }}>
              {player.playerName}
            </div>
            {/* Meta pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {player.position && <MetaPill label={player.position} />}
              {player.school && <MetaPill icon={GraduationCap} label={player.school} />}
              {player.classYear && <MetaPill icon={Calendar} label={player.classYear} />}
              {player.height && <MetaPill icon={Ruler} label={player.height} />}
              {player.weight && <MetaPill icon={Weight} label={`${player.weight} lbs`} />}
              {(player.hometown || player.state) && (
                <MetaPill icon={MapPin} label={[player.hometown, player.state].filter(Boolean).join(', ')} />
              )}
            </div>
          </div>
        </div>

        {/* Right: SLAM Composite Ring + Sub-Rings */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexShrink: 0 }}>
          {/* Main SLAM Ring */}
          <Ring val={slamComposite} color={slamTier.color} sz={130} sw={4}>
            <AnimN value={slamComposite} sz={38} color={slamTier.color} />
            <span style={{ fontSize: 8, color: C.t3, letterSpacing: 2, marginTop: -2 }}>SLAM</span>
          </Ring>

          {/* Sub-score rings */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'COG', val: cogScore || 0, color: C.cog },
              { label: 'CHAR', val: charScore || 0, color: C.char },
              { label: 'HDI', val: 0, color: C.hdi },
            ].map(sub => (
              <div key={sub.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Ring val={sub.val} color={sub.val > 0 ? sub.color : C.border} sz={44} sw={2.5}>
                  <span style={{ fontSize: 11, fontWeight: 500, color: sub.val > 0 ? sub.color : C.t3, fontFamily: 'monospace' }}>
                    {sub.val > 0 ? sub.val : '--'}
                  </span>
                </Ring>
                <span style={{ fontSize: 9, color: C.t3, letterSpacing: 2 }}>{sub.label}</span>
              </div>
            ))}
          </div>

          {/* DX Grade badge */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            padding: '12px 16px', borderRadius: 14,
            background: C.s1, border: `1px solid ${C.border}`,
          }}>
            <span style={{ fontSize: 8, color: C.t3, letterSpacing: 2, textTransform: 'uppercase' }}>DX GRADE</span>
            <span style={{
              fontSize: 28, fontWeight: 200, fontFamily: 'monospace',
              color: tier(player.dxGrade || 0).color,
            }}>
              {player.dxGrade || '--'}
            </span>
            <span style={{
              fontSize: 9, fontWeight: 600, letterSpacing: 1,
              color: tier(player.dxGrade || 0).color,
              padding: '2px 8px', borderRadius: 4,
              background: `${tier(player.dxGrade || 0).color}18`,
            }}>
              {player.dxLevel || tier(player.dxGrade || 0).label}
            </span>
            {player.archetype && (
              <span style={{ fontSize: 9, color: C.t2, marginTop: 2 }}>{player.archetype}</span>
            )}
          </div>
        </div>
      </div>

      {/* ═══ TAB BAR ═══ */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
        {TABS.map(tab => (
          <Pill key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </Pill>
        ))}
      </div>

      {/* ═══ TAB CONTENT ═══ */}
      {activeTab === 'overview' && (
        <OverviewTabContent player={player} cogScore={cogScore} charScore={charScore} />
      )}
      {activeTab === 'stats' && (
        <StatsTabContent player={player} />
      )}
      {activeTab === 'history' && (
        <HistoryTabContent player={player} />
      )}
    </div>
  );
}
